import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import CategorySidebar from './components/CategorySidebar';
import OrganizationProfile from './components/OrganizationProfile';
import BrandingSettings from './components/BrandingSettings';
import CustomDomainSettings from './components/CustomDomainSettings';
import LocationsManagement from './components/LocationsManagement';
import UsersManagement from './components/UsersManagement';
import RolesManagement from './components/RolesManagement';
import UserPreferences from './components/UserPreferences';
import TaxSettings from './components/TaxSettings';
import GeneralSetup from './components/GeneralSetup';
import CurrencySettings from './components/CurrencySettings';
import OpeningBalances from './components/OpeningBalances';
import RemindersSettings from './components/RemindersSettings';
import CustomerPortal from './components/CustomerPortal';
import VendorPortal from './components/VendorPortal';
import ManualTransactionSettings from './components/ManualTransactionSettings';
import TransactionNumberSeries from './components/TransactionNumberSeries';
import PDFTemplates from './components/PDFTemplates';
import EmailNotifications from './components/EmailNotifications';
import ReportingTags from './components/ReportingTags';
import WebTabs from './components/WebTabs';
import WorkflowRules from './components/WorkflowRules';
import WorkflowActions from './components/WorkflowActions';
import WorkflowLogs from './components/WorkflowLogs';
import AIConfigurationAssistant from './components/AIConfigurationAssistant';
import StatementImports from './components/StatementImports';
import CreditNotesManagement from './components/CreditNotesManagement';

const OrganizationSettings = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeCategory, setActiveCategory] = useState('organization');
  const [activeSubItem, setActiveSubItem] = useState('profile');
  
  const { category, subitem } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // URL parameter mapping for category and subitem navigation
  const urlMapping = {
    // Direct mappings for shortcuts
    'reminders': { category: 'setup-configurations', subitem: 'reminders' },
    'customer-portal': { category: 'setup-configurations', subitem: 'customer-portal' },
    'vendor-portal': { category: 'setup-configurations', subitem: 'vendor-portal' },
    // Category mappings
    'organization': { category: 'organization', subitem: 'profile' },
    'users-roles': { category: 'users-roles', subitem: 'users' },
    'user-preferences': { category: 'user-preferences', subitem: 'preferences' },
    'taxes-compliance': { category: 'taxes-compliance', subitem: 'taxes' },
    'setup-configurations': { category: 'setup-configurations', subitem: 'general' },
    'customization': { category: 'customization', subitem: 'transaction-number-series' },
    'automation': { category: 'automation', subitem: 'workflow-rules' },
  };

  // Initialize state from URL parameters
  useEffect(() => {
    if (category) {
      // Check for direct shortcut mappings first
      if (urlMapping?.[category] && !subitem) {
        setActiveCategory(urlMapping?.[category]?.category);
        setActiveSubItem(urlMapping?.[category]?.subitem);
      } else {
        // Use category and subitem directly
        setActiveCategory(category);
        if (subitem) {
          setActiveSubItem(subitem);
        } else {
          // Set default subitem based on category
          const defaultSubitems = {
            'organization': 'profile',
            'users-roles': 'users',
            'user-preferences': 'preferences',
            'taxes-compliance': 'taxes',
            'setup-configurations': 'general',
            'customization': 'transaction-number-series',
            'automation': 'workflow-rules'
          };
          setActiveSubItem(defaultSubitems?.[category] || 'profile');
        }
      }
    }
  }, [category, subitem]);

  // Update URL when internal navigation changes
  const handleCategoryChange = (newCategory) => {
    setActiveCategory(newCategory);
    
    // Set default subitem for the category
    const defaultSubitems = {
      'organization': 'profile',
      'users-roles': 'users',
      'user-preferences': 'preferences',
      'taxes-compliance': 'taxes',
      'setup-configurations': 'general',
      'customization': 'transaction-number-series',
      'automation': 'workflow-rules'
    };
    const newSubItem = defaultSubitems?.[newCategory] || 'profile';
    setActiveSubItem(newSubItem);
    
    // Update URL
    navigate(`/organization-settings/${newCategory}/${newSubItem}`, { replace: true });
  };

  const handleSubItemChange = (newSubItem) => {
    setActiveSubItem(newSubItem);
    
    // Update URL
    navigate(`/organization-settings/${activeCategory}/${newSubItem}`, { replace: true });
  };

  const renderContent = () => {
    const categoryItem = `${activeCategory}-${activeSubItem}`;
    
    switch (categoryItem) {
      case 'organization-profile':
        return <OrganizationProfile />;
      case 'organization-branding':
        return <BrandingSettings />;
      case 'organization-custom-domain':
        return <CustomDomainSettings />;
      case 'organization-locations':
        return <LocationsManagement />;
      case 'users-roles-users':
        return <UsersManagement />;
      case 'users-roles-roles':
        return <RolesManagement />;
      case 'user-preferences-preferences':
        return <UserPreferences />;
      case 'taxes-compliance-taxes':
        return <TaxSettings />;
      case 'setup-configurations-general':
        return <GeneralSetup />;
      case 'setup-configurations-currencies':
        return <CurrencySettings />;
      case 'setup-configurations-opening-balances':
        return <OpeningBalances />;
      case 'setup-configurations-reminders':
        return <RemindersSettings />;
      case 'setup-configurations-customer-portal':
        return <CustomerPortal />;
      case 'setup-configurations-vendor-portal':
        return <VendorPortal />;
      case 'setup-configurations-manual-transactions':
        return <ManualTransactionSettings />;
      case 'setup-configurations-statement-imports':
        return <StatementImports />;
      case 'customization-transaction-number-series':
        return <TransactionNumberSeries />;
      case 'customization-pdf-templates':
        return <PDFTemplates />;
      case 'customization-email-notifications':
        return <EmailNotifications />;
      case 'customization-reporting-tags':
        return <ReportingTags />;
      case 'customization-web-tabs':
        return <WebTabs />;
      case 'automation-workflow-rules':
        return <WorkflowRules />;
      case 'automation-workflow-actions':
        return <WorkflowActions />;
      case 'automation-workflow-logs':
        return <WorkflowLogs />;
      case 'module-settings-credit-notes':
        return <CreditNotesManagement />;
      default:
        return <OrganizationProfile />;
    }
  };

  // Get current feature name for title
  const getCurrentFeatureName = () => {
    const categoryItem = `${activeCategory}-${activeSubItem}`;
    const featureNames = {
      'setup-configurations-reminders': 'Reminders Settings',
      'setup-configurations-customer-portal': 'Customer Portal',
      'setup-configurations-vendor-portal': 'Vendor Portal',
      'setup-configurations-manual-transactions': 'Manual Transactions',
      'setup-configurations-statement-imports': 'Statement Imports',
      'module-settings-credit-notes': 'Credit Notes Management'
    };
    return featureNames?.[categoryItem] || 'Organization Settings';
  };

  return (
    <>
      <Helmet>
        <title>{getCurrentFeatureName()} - AccounTech AI</title>
        <meta name="description" content="Comprehensive administrative control over business configuration, user management, and system customization" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="flex h-[calc(100vh-4rem)]">
            {/* Settings Category Sidebar */}
            <CategorySidebar
              activeCategory={activeCategory}
              activeSubItem={activeSubItem}
              onCategoryChange={handleCategoryChange}
              onSubItemChange={handleSubItemChange}
            />
            
            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Content Header */}
              <div className="bg-surface border-b border-border p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-semibold text-foreground mb-2">
                      {getCurrentFeatureName()}
                    </h1>
                    <p className="text-muted-foreground">
                      Configure your business settings, manage users, and customize system behavior
                    </p>
                  </div>
                  
                  {/* AI Configuration Assistant Button */}
                  <AIConfigurationAssistant />
                </div>
              </div>
              
              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-6">
                  {renderContent()}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default OrganizationSettings;